import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
   Sheet,
   SheetContent,
   SheetDescription,
   SheetHeader,
   SheetTitle,
} from "@/components/ui/sheet";
import {
   Tooltip,
   TooltipContent,
   TooltipProvider,
   TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { getUserRoles, isAuthenticated } from '@/utils/auth';
import { removeToken } from '@/utils/tokenManager';
import { gql, useMutation } from '@apollo/client';
import {
   BarChart,
   Bell,
   CalendarCheck,
   ChevronLeft,
   Clock,
   Home,
   LayoutDashboard,
   LogOut,
   // Map,
   Receipt,
   Settings,
   UserCircle
} from "lucide-react";
import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { usePersonnelNotifications } from '../hooks/usePersonnelNotifications';

const LOGOUT_MUTATION = gql`
   mutation Logout {
      logout
   }
`;

const PersonnelSidebar = React.memo(({ isOpen, onToggle }) => {
   const navigate = useNavigate();
   const location = useLocation();
   const [logout] = useMutation(LOGOUT_MUTATION);
   const userRoles = getUserRoles();
   const [isAuth, setIsAuth] = useState(isAuthenticated());
   const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
   const { unreadCount } = usePersonnelNotifications();

   // Add effect to close sidebar on initial load
   useEffect(() => {
      if (isOpen) {
         onToggle(false);
      }
   }, []);

   const handleLogout = useCallback(async () => {
      try {
         await logout();
         removeToken();
         localStorage.removeItem('user');
         // console.log('Admin data before removing:', JSON.parse(localStorage.getItem('admin')));
         localStorage.removeItem('admin');
         // console.log('Admin data after removing:', JSON.parse(localStorage.getItem('admin')));
         setIsAuth(false);
         if (isOpen) onToggle();
         navigate('/auth');
      } catch (error) {
         console.error('Logout failed:', error);
      }
   }, [logout, navigate, isOpen, onToggle]);

   useEffect(() => {
      const authStatus = isAuthenticated();
      setIsAuth(authStatus);
      if (!authStatus) {
         if (isOpen) onToggle();
         navigate('/auth');
      }
   }, [navigate, isOpen, onToggle]);

   const getDashboardLink = () => {
      const roleLinks = {
         'Receiving_Clerk': "/personnel/receiving-releasing",
         'Releasing_Clerk': "/personnel/receiving-releasing",
         'Technical_Staff': "/personnel/technical-staff",
         'Forest_Ranger': "/personnel/technical-staff",
         'Chief_RPS': "/personnel/chief",
         'Chief_TSD': "/personnel/chief",
         'Accountant': "/personnel/accountant",
         'OOP_Staff_Incharge': "/personnel/accountant",
         'Bill_Collector': "/personnel/bill-collector",
         'Credit_Officer': "/personnel/bill-collector",
         'PENR_CENR_Officer': "/personnel/penr-cenr-officer",
         'Deputy_CENR_Officer': "/personnel/penr-cenr-officer",
         'Inspection_Team': "/personnel/inspection-team"
      };

      return userRoles.reduce((link, role) => roleLinks[role] || link, "/personnel/dashboard");
   };

   const shouldShowOrderOfPayment = () => {
      return userRoles.includes('Chief_RPS') || userRoles.includes('Chief_TSD');
   };

   const shouldShowExpiringPermits = () => {
      const excludedRoles = ['Chief_RPS', 'Chief_TSD', 'Bill_Collector'];
      return !userRoles.some(role => excludedRoles.includes(role));
   };

   const mainNavItems = [
      { to: "/personnel/home", icon: Home, text: "Home" },
      { to: getDashboardLink(), icon: LayoutDashboard, text: "Dashboard" },
      ...(userRoles.includes('Technical_Staff') ? [
         { to: "/personnel/inspection-scheduling", icon: CalendarCheck, text: "Inspection Schedule" }
      ] : []),
      ...(shouldShowExpiringPermits() ? [
         { to: "/personnel/expiring-permits", icon: Clock, text: "Permits Management" }
      ] : []),
      ...(shouldShowOrderOfPayment() ? [
         { to: "/personnel/order-of-payment", icon: Receipt, text: "Order of Payment" }
      ] : []),
      {
         to: "/personnel/notifications",
         icon: Bell,
         text: "Notifications",
         badge: unreadCount > 0 ? unreadCount : null
      },
      { to: "/personnel/reports", icon: BarChart, text: "Reports" },
      // { to: "/gis", icon: Map, text: "GIS Maps" },
   ];

   const accountNavItems = [
      { to: "/personnel/profile", icon: UserCircle, text: "Profile" },
      { to: "/personnel/settings", icon: Settings, text: "Settings" },
      { to: "/auth", icon: LogOut, text: "Logout", onClick: handleLogout }
   ];

   const SidebarItem = ({ item, isCollapsed }) => {
      const isActive = location.pathname === item.to;

      const handleClick = (e) => {
         if (item.onClick) {
            item.onClick(e);
         }
         // Close sidebar if on mobile
         if (isMobile) {
            onToggle(false);
         }
      };

      const content = (
         <NavLink
            to={item.to}
            onClick={handleClick}
            className={cn(
               "relative flex h-10 w-full items-center",
               "hover:bg-accent rounded-lg transition-colors",
               "px-3"
            )}
         >
            <div className="flex items-center w-full">
               <div className="w-[24px] flex items-center justify-center relative">
                  <item.icon className={cn(
                     "h-4 w-4",
                     isActive ? "text-accent-foreground" : "text-muted-foreground"
                  )} />
                  {item.badge && (
                     <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs
                        font-medium px-1.5 py-0.5 rounded-full min-w-[12px] h-[12px]
                        flex items-center justify-center">
                        {item.badge > 99 ? '99+' : item.badge}
                     </span>
                  )}
               </div>
               <span className={cn(
                  "ml-2 text-[15px] absolute left-[48px]",
                  "transition-all duration-300",
                  isCollapsed ? "opacity-0 -translate-x-2" : "opacity-100 translate-x-0",
                  isActive ? "text-accent-foreground" : "text-muted-foreground"
               )}>
                  {item.text}
               </span>
            </div>
         </NavLink>
      );

      if (isCollapsed) {
         return (
            <TooltipProvider delayDuration={0}>
               <Tooltip>
                  <TooltipTrigger asChild>
                     {content}
                  </TooltipTrigger>
                  <TooltipContent side="right" className="flex items-center">
                     {item.text}
                  </TooltipContent>
               </Tooltip>
            </TooltipProvider>
         );
      }

      return content;
   };

   const SidebarContent = ({ isCollapsed = false }) => (
      <div className="flex h-full flex-col">
         <div className={cn(
            "flex h-[12px] items-center",
            isCollapsed ? "justify-center" : "px-4"
         )}>
            <NavLink to="/personnel/home" className="flex items-center gap-2"> </NavLink>
         </div>
         <ScrollArea className="flex-1">
            <div className="space-y-1 px-2 pt-1 font-medium">
               {mainNavItems.map((item, i) => (
                  <SidebarItem key={i} item={item} isCollapsed={isCollapsed} />
               ))}
            </div>
         </ScrollArea>
         <div className={cn(
            "mt-auto border-t px-2 py-2"
         )}>
            {accountNavItems.map((item, i) => (
               <SidebarItem key={i} item={item} isCollapsed={isCollapsed} />
            ))}
         </div>
         <Button
            variant="ghost"
            className={cn(
               "h-10 w-full border-t",
               isCollapsed ? "px-2" : "px-4"
            )}
            onClick={onToggle}
         >
            <ChevronLeft className={cn(
               "h-4 w-4 transition-all",
               isOpen ? "rotate-0" : "rotate-180"
            )} />
         </Button>
      </div>
   );

   // Add useEffect to handle window resize
   useEffect(() => {
      const handleResize = () => {
         setIsMobile(window.innerWidth < 1024);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
   }, []);

   // Update the render logic
   if (isMobile) {
      return (
         <Sheet open={isOpen} onOpenChange={onToggle}>
            <SheetContent side="left" className="p-0 w-[270px] flex flex-col">
               <SheetHeader className="h-16 border-b">
                  <div className="flex items-center h-full px-6">
                     <SheetTitle className="text-left">Menu</SheetTitle>
                     <SheetDescription className="sr-only">
                        Navigation menu for personnel dashboard
                     </SheetDescription>
                  </div>
               </SheetHeader>
               <div className="flex-1 overflow-auto">
                  <SidebarContent />
               </div>
            </SheetContent>
         </Sheet>
      );
   }

   return (
      <div className={cn(
         "fixed top-16 left-0 z-30 h-[calc(100%-4rem)] border-r bg-background",
         isOpen ? "w-[270px]" : "w-[64px]",
         "transition-[width] duration-300 ease-in-out"
      )}>
         <SidebarContent isCollapsed={!isOpen} />
      </div>
   );
});

export default PersonnelSidebar;
